import "./Exhibitions.css"

import ReactDOM from "react-dom/client";

import {useEffect, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import useAccessToken from "../../hooks/useToken";
import {useDashboardLocale} from "../../context/dashboard_locale";

import DashboardHeader from "../../components/dashboardHeader/DashboardHeader";
import ExhibitionList from "../../components/exhibitionList/ExhibitionList";

import Dialog from "../../components/dialog/Dialog";
import Pagination from "../../components/pagination/Pagination";
import EmptyState from "../../components/emptyState/EmptyState";
import LoadingState from "../../components/loadingState/LoadingState";
import SearchBar from "../../components/searchBar/SearchBar";

import ExhibitionsViewModel from "./ExhibitionsViewModel";
import {deleteExhibition, fetchExhibitions} from "../../services/api/Exhibition";
import interruptAuth from "../../utils/Interrupt_auth";
import {capitalize} from "../../utils/strings";

function showConfirmDeleteDialog(localizedString, deleteCallback) {
  const modalRoot = document.createElement("div");
  document.body.appendChild(modalRoot);
  const root = ReactDOM.createRoot(modalRoot);

  function closeModal() {
    root.unmount();
    document.body.removeChild(modalRoot);
  }

  function confirmHandler() {
    closeModal();
  }

  function destructiveHandler() {
    closeModal();
    deleteCallback();
  }

  root.render(
    <Dialog 
      title={localizedString["action_confirm"]}
      description={localizedString["delete_exhibition_warning"]}
      confirmTitle={capitalize(localizedString["no"])}
      destructiveTitle={capitalize(localizedString["yes"])}
      confirmHandler={confirmHandler}
      destructiveHandler={destructiveHandler}
    />
  );
}

export default function Exhibitions() {
    const viewModel = new ExhibitionsViewModel();

    const {strings} = useDashboardLocale();
    const [searchParams] = useSearchParams();
    const [exhibitions, setExhibitions] = useState([]);
    const [paginationItems, setPaginationItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const {token} = useAccessToken();

    const page = parseInt(searchParams.get("page")) || 1;
    const query = searchParams.get("query") || "";

    useEffect(function() {
      interruptAuth(token, navigate);
    })

    function onPageClick(page) {
      navigate(viewModel.getURL(page, query));
    }

    function onViewItemClick(id) {
      navigate(viewModel.getViewExhibitionURL(id));
    }

    function forceRefreshList() {
      setIsLoading(true);
      fetchExhibitions(token, page, query).then(res => {
        setPaginationItems(viewModel.createPaginationItems(res))
        const exhibitions = res.data["response"]["data"].map((item) => {
          const exhibitionModel = viewModel.prepareExhibitionModel(item);
          exhibitionModel.onViewItemClick = onViewItemClick;
          exhibitionModel.onEditItemClick = onEditItemClick;
          exhibitionModel.onQrItemClick = onQrItemClick;
          exhibitionModel.onDeleteClick = onDeleteClick;
          return exhibitionModel;
        });
        setExhibitions(exhibitions);
        setIsLoading(false);
      });
    }

    function onEditItemClick(id) {
      navigate(`/dashboard/edit?id=${id}`);
    }

    function onDeleteClick(id) {
      showConfirmDeleteDialog(strings, () => {
        setIsLoading(true);
        deleteExhibition(token, id)
          .then(res => {
            navigate(viewModel.getURL(1, null));
            forceRefreshList();
          })
          .catch(err => {
            forceRefreshList();
          });
      });
    }

    function onQrItemClick(id) {
      viewModel.downloadQRCode(id, (err) => {
        if (err) {
          alert(err.toString());
        }
      });
    }

    function onSearch(text) {
      navigate(viewModel.getURL(1, text));
    }

    useEffect(() => {
      forceRefreshList();
    }, [page, query]);

    let mainContent;
    if (isLoading) {
        mainContent = (
          <div className="flex flex-col flex-grow justify-center">
            <LoadingState text={`${capitalize(strings["loading"])}...`}/>
          </div>
        );
    } else if (exhibitions.length) {
      mainContent = (
        <div className="space-y-6">
          <ExhibitionList exhibitions={exhibitions} />
          <Pagination
              currentPage={page}
              onPageClick={onPageClick}
              onNextPageClick={() => {onPageClick(page + 1)}}
              onPrevPageClick={() => {onPageClick(page - 1)}}
              items={paginationItems}
          />
        </div>
      );
    } else {
      mainContent = (
        <div className="flex flex-col flex-grow justify-center">
          <EmptyState text={strings["no_exhibition_available"]} />
        </div>
      );
    }
    return (
        <div className="flex flex-col min-h-screen">
          <DashboardHeader />
          <div className="flex flex-col flex-grow container mx-auto py-6 space-y-6">
            <SearchBar 
              placeholder={strings["search_exhibitions_placeholder"]}
              onSearch={onSearch}
            >
              {query}
            </SearchBar>
            { mainContent }
          </div>
        </div>
    )
}
